import classnames from 'classnames';
import { useCallback, useMemo, useState } from 'react';

import { AddContactAttributesModal } from 'components/contact';
import { SidebarToggleTrigger } from 'components/conversation/components/sidebar/sidebarToggleTrigger';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useConversationSidebarSettings } from 'utils/useConversationSidebarSettings';
import { useServerStorage } from 'utils/useServerStorage';

import { SuggestionCards } from './cards/KnowledgeCard';
import { CustomIntegrationCards } from './components/customIntegrationCards/customIntegrationCards';
import { CustomerInfoCards } from './components/customerInfoCards/customerInfoCards';
import { IntegrationCards } from './components/integrationCards/integrationCards';
import type {
  SidebarMode,
  SidebarSectionId,
} from './conversationSidebar.types';
import { defaultSectionOrder } from './conversationSidebar.types';
import { SidebarModeContext } from './hooks/useSidebarMode';

export const ConversationSidebar = (): JSX.Element => {
  const metaConversation = useAppSelector(
    (state) => state.conversation.metaConversation,
  );
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('default');
  const { cardOpenState, isSidebarOpen, setCardOpen, toggleSidebar } =
    useConversationSidebarSettings();

  const isCardOpen = useCallback(
    (key: string) => cardOpenState[key] ?? false,
    [cardOpenState],
  );

  const toggleCard = useCallback(
    (key: string) => {
      setCardOpen(key, !(cardOpenState[key] ?? false));
    },
    [cardOpenState, setCardOpen],
  );
  const storage = useServerStorage('user');
  const sectionOrder = useMemo(
    () =>
      Array.from(
        new Set([
          ...(storage.data.conversation?.sidebarSectionOrder ?? []),
          ...defaultSectionOrder,
        ]),
      ),
    [storage.data.conversation?.sidebarSectionOrder],
  );

  const rootClassName = classnames('conversation-sidebar-holder', {
    closed: !isSidebarOpen,
  });

  const renderCardSection = (type: SidebarSectionId, index: number) => {
    switch (type) {
      case 'customerInfo':
        return (
          <CustomerInfoCards
            key={type}
            index={index}
            isMinimized={!isSidebarOpen}
            metaConversation={metaConversation}
            isCardOpen={isCardOpen}
            toggleCard={toggleCard}
          />
        );

      case 'knowledge':
        return (
          <SuggestionCards
            key={type}
            index={index}
            isMinimized={!isSidebarOpen}
          />
        );
      case 'nativeIntegration':
        return (
          <IntegrationCards
            key={type}
            index={index}
            isMinimized={!isSidebarOpen}
            metaConversation={metaConversation}
            isCardOpen={isCardOpen}
            toggleCard={toggleCard}
          />
        );
      case 'customIntegration':
        return (
          <CustomIntegrationCards
            key={type}
            index={index}
            isMinimized={!isSidebarOpen}
            metaConversation={metaConversation}
          />
        );
    }
  };

  return (
    <div className={rootClassName}>
      <SidebarToggleTrigger
        isMenuOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        sidebarMode={sidebarMode}
        setSidebarMode={setSidebarMode}
      />
      <SidebarModeContext.Provider value={sidebarMode}>
        {sectionOrder.map((id, index) => renderCardSection(id, index))}
      </SidebarModeContext.Provider>
      <AddContactAttributesModal />
    </div>
  );
};
